import useSWR, { SWRConfiguration } from "swr";
import useFetcher from "@/utils/use-fetcher";
import { AxiosRequestConfig } from "axios";
import { Company } from "@/features/types";
import { useMemo } from "react";
import {
  calcUrl,
  withListAllFetcher,
} from "@/features/ui/helpers/fetcher-helpers";
import { UrlParser } from "@/features/ui/helpers/url-parser";
import { StringTransformer } from "@/features/ui/helpers/string-transformer";
import {
  getListForwarderListKey,
  ListForwarderListPathParameter,
  ListForwarderListRequest,
  ListForwarderListResponse,
} from "@/features/invoices/triangular-trades/hooks/use-list-forwarder";

export interface ListAllForwarderListRequest
  extends Omit<ListForwarderListRequest, "pageSize" | "pageNumber"> {}
type ListAllForwarderListResponse = Company[];
export interface ListAllForwarderListPathParameter
  extends ListForwarderListPathParameter {}

export const listAllForwarderListTemplateUrl = `/:by/:companyId/forwarder`;

export function getListAllForwarderListKey(
  parameter?: ListAllForwarderListPathParameter,
  request?: ListAllForwarderListRequest
) {
  if (parameter && request) {
    return {
      url: calcUrl(listAllForwarderListTemplateUrl, parameter),
      params: request,
    };
  }
  return null;
}

export function parseListAllForwarderListKey(
  key: AxiosRequestConfig<ListAllForwarderListRequest>
) {
  const urlParser = new UrlParser<ListAllForwarderListPathParameter>(
    listAllForwarderListTemplateUrl,
    {
      by: StringTransformer.parseBy,
      companyId: StringTransformer.parseId,
    }
  );
  return {
    parameter: urlParser.parse(key.url!),
    request: key.params,
  };
}

export default function useListAllForwarderList(
  parameter?: ListAllForwarderListPathParameter,
  request?: ListAllForwarderListRequest,
  config: SWRConfiguration = { revalidateOnFocus: false }
) {
  const listFetcher = useFetcher<
    ListForwarderListRequest,
    ListForwarderListResponse
  >();
  const fetcher = useMemo(() => {
    return withListAllFetcher(
      (config: AxiosRequestConfig<ListForwarderListRequest>) => {
        const { parameter, request } = parseListAllForwarderListKey(config);
        return listFetcher(getListForwarderListKey(parameter, request)!);
      },
      (response) => {
        return response.forwarderList;
      }
    );
  }, [listFetcher]);

  return useSWR(
    getListAllForwarderListKey(parameter, request),
    fetcher,
    config
  );
}
